import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { hasSomeRights } from 'nallian-shared-ui/lib/lib/routesHelper';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { InputFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import PrincipalRolesTab from '../principals/roles/PrincipalRolesTab';
import ReadOnlyfieldValueComponent from '../../../app/components/ReadOnlyFieldValueComponent';
import IdpUserGroupMessage from './IdpUserGroupMessage';

const FORM_NAME = 'userGroup';
const REQUIRED_FIELDS = ['name'];

const USER_GROUP_RIGHTS = [
  'user-groups/read/non-system-organisation',
  'user-groups/read/system-organisation',
  'user-groups/read/own-organisation',
];

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);
  return errors;
};

const UserGroupForm = ({
  editMode,
  organisationId,
  open,
  returnRoute,
  dataLoading,
  header,
  onSubmit,
  defaultData,
  submitProcessing,
  forbidManageBecauseSystemOrg,
}) => {
  const { t } = useTranslation();

  const rights = useSelector(GetRights);

  const readOnly = useMemo(
    () => forbidManageBecauseSystemOrg || !hasSomeRights(rights, USER_GROUP_RIGHTS),
    [rights, forbidManageBecauseSystemOrg]
  );

  if (!open) return null;

  return (
    <FormModal
      t={t}
      header={header}
      dataLoading={dataLoading}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={defaultData}
      validate={validate}
      form={FORM_NAME}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
    >
      {defaultData?.isManagedByIdentityProvider && <IdpUserGroupMessage />}
      <Grid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            label={t('name')}
            name="name"
            valueComponent={readOnly ? ReadOnlyfieldValueComponent : InputFieldValueComponent}
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <PrincipalRolesTab
            hideUserGroups
            editMode={editMode}
            organisationId={organisationId}
            formName={FORM_NAME}
            readOnly={readOnly}
          />
        </Grid.Column>
      </Grid>
    </FormModal>
  );
};

UserGroupForm.propTypes = {
  returnRoute: PropTypes.string.isRequired,
  organisationId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  header: PropTypes.string.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool.isRequired,
  defaultData: PropTypes.object,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  open: PropTypes.bool,
  editMode: PropTypes.bool,
};

UserGroupForm.defaultProps = {
  open: false,
  dataLoading: false,
  submitProcessing: false,
  defaultData: undefined,
  editMode: false,
};

export default UserGroupForm;
